import {Input, Radio, Select, Spin, TimePicker} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createControlledFormField, createNumberControlledInput} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, NullAsDefault, renderSelf, shallowDiff} from 'views/Shared'
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {ChipGroup, Field, Section} from "../../../../components/form/ModernForm";
import Pure from "../../../../components/Pure";
import {IoIosTimer} from "react-icons/io";
import {
    IoDocumentTextOutline,
    IoInformationCircleOutline,
    IoShieldCheckmarkOutline,
    IoStatsChartOutline
} from "react-icons/io5";
import {createDateComponent} from "../../../../components/form/fields/DateField";
import {StatusTypes} from "../../appFieldTypes";
import {BsCheckCircleFill, BsInfoCircle, BsLightningFill, BsPlusLg, BsSearch, BsTrash} from "react-icons/bs";
import classNames from "classnames";
import TextArea from "antd/lib/input/TextArea";
import {SelectWidget} from "../../custom/ReactSelectComponents";
import {dayjsFromMinutes, minutesFromDayjs} from "../../../../helpers/datetimeHelpers";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {course_quiz_questions_search_Api} from "../../../../apis";
import {notifyOnError} from "../../factory/createErrorEvent";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";

// Options THÔ (label = i18n key 'pending'/'active'/'closed', chưa dịch) - dùng CHUNG cho
// _initialValues.status_options (form cũ) VÀ ChipGroup ở CourseClassExamOverviewTab bên dưới.
// ChipGroup tự gọi t(label) bên trong nó (xem ModernForm.js) nên PHẢI truyền label thô, không truyền
// label đã dịch sẵn - truyền nhầm bản đã dịch sẽ bị t() gọi ĐÈ thêm lần nữa lên chuỗi tiếng Việt.
const EXAM_STATUS_OPTIONS = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]

// ID tạm cho câu hỏi/đáp án ở phía client (dùng làm React key + phân biệt item khi CRUD trong
// state) - không dùng crypto.randomUUID() vì bản đóng gói Cordova/webview cũ có thể không có.
const genId = () => _.uniqueId('q_')

const formSchema = {
    title: {
        title: 'exam title',
        description: 'exam title description',
        desClassName: 'text-xs text-color-400 italic mt-1.5 leading-relaxed',
        children: [
            {
                name: 'title',
                isRequired: true,
                component: createControlledFormField({
                    placeholder: 'exam title',
                }),
            },
        ],
    },
    description: {
        title: 'short description',
        description: 'exam short description guide',
        desClassName: 'text-xs text-color-400 italic mt-1.5 leading-relaxed',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    className: 'scrollBarThin',
                    rows: 3,
                    placeholder: 'short description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },
    started_finished_date: {
        inline: true,
        description: 'exam time window description',
        desClassName: 'text-xs text-color-400 italic mt-1.5 leading-relaxed',
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                name: 'time_open',
                label: 'started at',
                component: createDateComponent(),
            },
            {
                name: 'time_close',
                label: 'finished at',
                component: createDateComponent(),
            },
        ],
    },
    duration_minutes: {
        inline: true,
        description: 'exam duration description',
        desClassName: 'text-xs text-color-400 italic mt-1.5 leading-relaxed',
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        children: [
            {
                name: 'duration_minutes',
                label: 'duration (minutes)',
                component: ({name, value, onChange}) => {
                    return (
                        <Translate>
                            {(t) => (
                                <TimePicker
                                    placeholder={t('ex: 00:30:00')}
                                    suffixIcon={<IoIosTimer className="text-color-500"/>}
                                    showNow={false}
                                    allowClear={true}
                                    showSecond={false}
                                    className="w-full"
                                    value={dayjsFromMinutes(value)}
                                    onChange={(value) => {
                                        const minutes = minutesFromDayjs(value)
                                        onChange(createValue(name, minutes))
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    status: {
        children: [
            {
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Translate>
                            {t => (
                                <div className="flex flex-col gap-2">
                                    <Radio.Group
                                        name={name}
                                        value={value}
                                        options={options}
                                        onChange={onChange}
                                        optionType="button"
                                        buttonStyle="solid"
                                        className="custom-radio-button"
                                    />
                                    {/* Giải thích từng trạng thái để người dùng hiểu hệ quả trước khi
                                        chuyển - không chỉ là 3 nút không rõ nghĩa. */}
                                    <div className="p-2.5 background-50 rounded-lg flex flex-col gap-1 text-xs tracking-wider">
                                        <div className="flex gap-1.5">
                                            <span className="font-semibold text-amber-600 flex-shrink-0">{t('pending')}:</span>
                                            <span className="text-color-300">{t('exam status pending desc')}</span>
                                        </div>
                                        <div className="flex gap-1.5">
                                            <span className="font-semibold text-green-600 flex-shrink-0">{t('active')}:</span>
                                            <span className="text-color-300">{t('exam status active desc')}</span>
                                        </div>
                                        <div className="flex gap-1.5">
                                            <span className="font-semibold text-color-400 flex-shrink-0">{t('closed')}:</span>
                                            <span className="text-color-300">{t('exam status closed desc')}</span>
                                        </div>
                                        <div className="mt-1 pt-1.5 border-t border-color-50 text-color-200 italic">
                                            {`* ${t('note')}: ${t('active_status_requirement')}`}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    questions_json: {
        children: {
            name: 'questions_json',
            component: ({name, value, onChange,}) => (
                <QuestionsJSON
                    {...{
                        name,
                        defaultValue: value,
                        onChange: (value) => {
                            return onChange(createValue('questions_json', value))
                        },
                    }}
                />
            ),
        },
    },


    // Xếp DỌC (1 cột) + mô tả ngắn RIÊNG dưới từng ô - trước để 2 ô cùng hàng kèm 1 đoạn mô tả
    // chung dài, nhồi 1 dòng đọc rất mệt.
    score: {
        inline: true,
        className: 'grid grid-cols-1 gap-4',
        children: [
            {
                label: 'max score',
                name: 'max_score',
                description: 'exam max score hint',
                component: createNumberControlledInput({
                    placeholder: 'max score',
                    defaultValue: 10,
                }),
            },
            {
                label: 'passed score',
                name: 'passed_score',
                description: 'exam passed score hint',
                component: createNumberControlledInput({
                    placeholder: 'passed score',
                    defaultValue: 5,
                }),
            },
        ],
    },

    max_attempts: {
        title: 'max No of attempts',
        description: 'max attempts description',
        desClassName: 'text-xs text-color-400 italic mt-1.5 leading-relaxed',
        children: [
            {
                name: 'max_attempts',
                component: createNumberControlledInput({
                    placeholder: 'max score',
                    defaultValue: 10,
                }),
            },
        ],
    },
}

// --- Debounce Utility Hook (Mimics lodash.debounce) ---
const useDebouncedCallback = (callback, delay) => {
    const timeoutRef = useRef(null);
    const callbackRef = useRef(callback);

    // Update the callback ref whenever the callback changes
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Return the debounced function
    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            callbackRef.current(...args);
        }, delay);
    }, [delay]);
};

// Helper function to get the Nth letter (A, B, C...)
const getLetterValue = (index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
};


// --- Default Data Structure ---
const defaultOption = (index = 0) => ({
    value: getLetterValue(index),
    label: '',
    is_correct: 0,
});

const defaultQuestion = (type = 'single', t = renderSelf) => {
    const initialOps = [defaultOption(0), defaultOption(1)];

    return {
        id: genId(),
        q: '',
        type,
        max_score: 1,
        ops: type === 'single' || type === 'multiple'
            ? initialOps
            : (type === 'bool'
                ? [
                    {value: 'true', label: t('True'), is_correct: 1},
                    {value: 'false', label: t('False'), is_correct: 0},
                ]
                : []),
    };
};

// --- Option Editor Component ---
const OptionEditor = React.memo(({question, qIndex, onQuestionUpdate, errors}) => {
    const t = useTranslate();
    const {type, ops} = question;
    const {ops_correct, ops_labels} = errors;

    // 1. General handler for non-debounced changes (is_correct)
    const handleOptionChange = useCallback((opIndex, key, value) => {
        let newOps = ops.map((op, i) =>
            i === opIndex ? {...op, [key]: value} : op
        );

        if (key === 'is_correct' && (type === 'single' || type === 'bool') && value === true) {
            newOps = newOps.map((op, i) => ({
                ...op,
                is_correct: i === opIndex ? 1 : 0,
            }));
        }

        onQuestionUpdate(qIndex, {...question, ops: newOps});
    }, [question, qIndex, ops, type, onQuestionUpdate]);

    // 2. Debounced handler for label input
    const handleLabelChangeCommit = useCallback((opIndex, label) => {
        let newOps = ops.map((op, i) =>
            i === opIndex ? {...op, label} : op
        );
        onQuestionUpdate(qIndex, {...question, ops: newOps});
    }, [question, qIndex, ops, onQuestionUpdate]);

    // Apply debounce to the commit function
    const debouncedLabelChange = useDebouncedCallback(handleLabelChangeCommit, 400);

    // Local state for immediate input feedback (to prevent UI lag while typing)
    const [localOps, setLocalOps] = useState(ops);

    useEffect(() => {
        // Keep local state synchronized with prop state when question object changes
        setLocalOps(ops);
    }, [ops]);


    const addOption = useCallback(() => {
        if ((type === 'single' || type === 'multiple') && ops.some(op => op.label.trim() === '')) {
            return;
        }
        onQuestionUpdate(qIndex, {...question, ops: [...ops, defaultOption(ops.length)]});
    }, [question, qIndex, ops, onQuestionUpdate, type]);

    const removeOption = useCallback((opIndex) => {
        if (ops.length <= 2) return;

        const filteredOps = ops.filter((_, i) => i !== opIndex);
        const newOps = filteredOps.map((op, i) => ({
            ...op,
            value: getLetterValue(i),
        }));

        onQuestionUpdate(qIndex, {...question, ops: newOps});
    }, [question, qIndex, ops, onQuestionUpdate]);

    if (type === 'essay') {
        return <div
            className="mt-2 text-sm italic text-color-200">{t('Essay type does not require answer options.')}</div>;
    }

    if (type === 'bool') {
        return (
            <div className="mt-2 p-3 border border-color-50 background-50 rounded-xl">
                <h4 className="font-semibold text-sm text-color-000 ">{t('True/False Options')}</h4>
                <p className="mb-3 text-xs text-color-200 italic">
                    {t("Select 'True' or 'False' as the correct answer.")}
                </p>
                {ops.map((op, opIndex) => (
                    <div key={op.value}
                         className="mb-2 px-3 py-2 flex items-center gap-3 background border border-color-50 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="radio"
                                name={`q${qIndex}-bool-correct`}
                                checked={!!op.is_correct}
                                onChange={(e) => handleOptionChange(opIndex, 'is_correct', e.target.checked)}
                                className="form-radio text-green-600 border-gray-300 rounded-full transition duration-150 ease-in-out"
                            />
                            <span className="text-sm text-color-200 whitespace-no-wrap">{t('Correct Answer')}</span>
                        </label>
                        <Input
                            readOnly
                            type="text"
                            value={op.label}
                            className="flex-1 px-3 font-medium text-sm text-gray-700 cursor-not-allowed"
                        />
                        <BsCheckCircleFill size={20} className={op.is_correct ? "text-green-600" : "text-color-500"}/>
                    </div>
                ))}
                {ops_correct && (
                    <p className="text-sm text-red-500 mt-2 p-2 bg-red-100 border-l-4 border-red-500 rounded-md">
                        {ops_correct}
                    </p>
                )}
            </div>
        );
    }

    const hasEmptyOptions = ops.some(op => op.label.trim() === '');

    return (
        <div className="p-4 bg-slate-50 rounded-xl">
            <h4 className="mb-1 font-bold text-base text-gray-700 leading-tight">
                {t('Answer Options')}
            </h4>
            <p className="mb-4 text-xs text-color-300 tracking-wider">
                {t('Select')} {type === 'single' ? t('one') : t('multiple')} {t('correct answer(s).')}
            </p>

            {(ops_correct || ops_labels) && (
                <div className="mb-3 p-2 bg-primary-50 rounded-lg">
                    {ops_correct && <p className="text-xs text-red-600 italic">{t(ops_correct)}</p>}
                    {ops_labels && <p className="text-xs text-red-600 italic">{t(ops_labels)}</p>}
                </div>
            )}

            {/* RENDER BASED ON localOps for immediate UI feedback */}
            {localOps.map((op, opIndex) => (
                <div key={op.value}
                     className="flex items-center gap-3 mb-3 p-2 border border-color-50 background rounded-lg">

                    <span className="text-center font-bold text-lg text-color-000 leading-tight">
                        {op.value}.
                    </span>

                    <Input
                        type="text"
                        value={localOps[opIndex].label}
                        onChange={(e) => {
                            // 1. Update local state immediately (for smooth UI)
                            const newLocalOps = localOps.map((o, i) =>
                                i === opIndex ? {...o, label: e.target.value} : o
                            );
                            setLocalOps(newLocalOps);

                            // 2. Call debounced function to update parent state
                            debouncedLabelChange(opIndex, e.target.value);
                        }}
                        placeholder={`${t('option content')} ${op.value}`}
                        className="flex-1"
                    />
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <Input
                            type={type === 'single' ? 'radio' : 'checkbox'}
                            name={`q${qIndex}-correct`}
                            checked={!!op.is_correct}
                            onChange={(e) => handleOptionChange(opIndex, 'is_correct', e.target.checked)}
                            className={classNames(
                                `text-green-600 border-gray-300 rounded focus:ring-green-500 transition duration-150 ease-in-out`,
                                type === 'single' ? 'form-radio' : 'form-checkbox'
                            )}
                        />
                        <span className="text-sm text-color-200 whitespace-no-wrap">{t('Correct Answer')}</span>
                    </label>
                    <BsTrash
                        onClick={() => removeOption(opIndex)}
                        disabled={ops.length <= 2}
                        size={16}
                        className={classNames(
                            `inline-block transition cursor-pointer`,
                            ops.length <= 2 ? 'text-color-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'
                        )}
                        title={t("Remove option")}
                    />
                </div>
            ))}

            <button
                type="button"
                onClick={addOption}
                disabled={hasEmptyOptions}
                className={classNames(
                    `mt-3 w-90 mx-auto flex flex-center gap-2 p-2 text-sm font-medium rounded-xl shadow-md transition`,
                    hasEmptyOptions
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200'
                )}>
                <BsPlusLg size={13}/>
                {t('Add Option')}
            </button>
            {hasEmptyOptions && (
                <p className="mt-2 text-center text-xs text-red-500 italic">
                    {t('Please fill in content for all options before adding a new one.')}
                </p>
            )}
        </div>
    );
});


// --- Single Question Card Component ---
const QuestionCard = ({question, index, onQuestionUpdate, onQuestionRemove, errors}) => {
    const t = useTranslate();
    const {q, type, max_score} = question;

    // Handler for non-debounced changes (type, max_score)
    const handleChange = useCallback((key, value) => {
        let updatedQuestion = {...question, [key]: value};

        if (key === 'type') {
            updatedQuestion = defaultQuestion(value, t);
            updatedQuestion.id = question.id;
        }

        onQuestionUpdate(index, updatedQuestion);
    }, [question, index, onQuestionUpdate]);

    // Handler to commit 'q' text change
    const handleQChangeCommit = useCallback((value) => {
        onQuestionUpdate(index, {...question, q: value});
    }, [question, index, onQuestionUpdate]);

    // Apply debounce to the commit function
    const debouncedQChange = useDebouncedCallback(handleQChangeCommit, 400);

    // Local state for immediate input feedback (Q text)
    const [localQ, setLocalQ] = useState(q);

    useEffect(() => {
        // Keep local state synchronized with prop state
        setLocalQ(q);
    }, [q]);

    return (
        <div className="mb-6 p-3 md:p-4 border border-color-100 background shadow-md rounded-xl transition">
            <div className="flex justify-between items-center border-b mb-2 pb-3">
                <h3 className="text-lg font-bold text-gray-800">
                    {t('Question')} #{index + 1}
                </h3>
                <BsTrash
                    size={20}
                    onClick={() => showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete question',
                        message: 'are you sure you want to delete this question',
                        onOk: () => onQuestionRemove(index),
                    })}
                    className="text-red-500 hover:text-red-700 transition active:scale-95 cursor-pointer"
                />
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Question Type (type) - Non-debounced */}
                    <div className="space-y-1">
                        <label htmlFor={`type-${index}`} className="text-sm text-color-300 tracking-wide">
                            {t('Question Type')}
                        </label>
                        <SelectWidget
                            id={`type-${index}`}
                            name="type"
                            allowClear={true}
                            filterOption={false}
                            optionFilterProp="label"
                            value={type}
                            onChange={(e) => {
                                handleChange('type', e)
                            }}
                            className="w-full">
                            <Select.Option value="single">{t('Single Choice')}</Select.Option>
                            <Select.Option value="multiple">{t('Multiple Choice')}</Select.Option>
                            <Select.Option value="bool">{t('True/False')}</Select.Option>
                            <Select.Option value="essay">{t('Essay')}</Select.Option>
                        </SelectWidget>
                    </div>

                    {/* Max Score (max_score) - Non-debounced */}
                    <div className="space-y-1">
                        <label htmlFor={`score-${index}`} className="text-sm text-color-300 tracking-wide">
                            {t('Max Score')}
                        </label>
                        <Input
                            onWheel={(e) => e.currentTarget.blur()}
                            id={`score-${index}`}
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={max_score}
                            onChange={(e) => handleChange('max_score', parseFloat(e.target.value))}
                            placeholder="1.0"
                            className="w-full"
                        />
                        {errors.max_score && <p className="mt-1 text-xs text-red-500 italic">{t(errors.max_score)}</p>}
                    </div>
                </div>

                {/* Question Text (q) - Debounced */}
                <div className="space-y-1">
                    <label htmlFor={`q-${index}`} className="text-sm text-color-300 tracking-wide">
                        {t('Question Content')}
                    </label>
                    <TextArea
                        rows={2}
                        id={`q-${index}`}
                        value={localQ}
                        onChange={(e) => {
                            setLocalQ(e.target.value);
                            debouncedQChange(e.target.value);
                        }}
                        placeholder={t('Enter question content here...')}
                        className="w-full px-4 py-3 border rounded-xl transition"
                    />
                    {errors.q && <p className="mt-1 text-xs text-red-500 italic">{t(errors.q)}</p>}
                </div>

                {/* Answer Options (ops) */}
                <OptionEditor
                    question={question}
                    qIndex={index}
                    onQuestionUpdate={onQuestionUpdate}
                    errors={errors}
                />
            </div>
        </div>
    );
};

// --- Inline Add Question Button Component ---
const InlineAddButton = React.memo(({index, onAdd}) => {
    const t = useTranslate();

    return (
        <div className="flex justify-center my-4">
            <button
                type="button"
                onClick={() => onAdd(index + 1)} // Insert AFTER the current index
                className="flex items-center gap-2 px-6 py-3 font-medium text-white shadow-md transition hover:scale-2xs bg-indigo-500 hover:bg-indigo-600 rounded-xl"
                title={t(`Add question at position #${index + 2}`)}>
                <BsPlusLg size={19}/>
                {t('Add Question Here')}
            </button>
        </div>
    )
});


// --- Validation Utility ---
const validateQuestion = (question, t = renderSelf) => {
    const errors = {};

    // 1. Question Text (q) must not be empty
    if (!question.q.trim()) {
        errors.q = t("Question content cannot be empty.");
    }

    // 2. Max Score (max_score) must be a positive number
    if (question.max_score <= 0 || isNaN(question.max_score)) {
        errors.max_score = t("Max score must be greater than 0.");
    }

    // 3. Options Validation
    const {type, ops} = question;

    if (type === 'single' || type === 'multiple' || type === 'bool') {
        const safeOps = Array.isArray(ops) ? ops : []
        // Check for correct answer selected
        const correctAnswers = safeOps.filter(op => !!op.is_correct);
        if (correctAnswers.length === 0) {
            errors.ops_correct = t("You must select at least one correct answer.");
        }

        // Check for empty labels (only for single/multiple, bool labels are fixed)
        if (type === 'single' || type === 'multiple') {
            const emptyLabels = safeOps.some(op => op.label.trim() === '');
            if (emptyLabels) {
                errors.ops_labels = t("All options must have content.");
            }
        }
    }

    return errors;
};


// --- Quiz Question Search Bar ---
const QuizQuestionSearchBar = ({onUseQuestion}) => {
    const t = useTranslate()
    const [keyword, setKeyword] = useState('')
    const [results, setResults] = useState([])
    const [open, setOpen] = useState(false)
    const [dropdownStyle, setDropdownStyle] = useState({})
    const wrapperRef = useRef(null)

    const {handleAsyncAction: doSearch, isLoading} = useAsyncAction({
        apiInfo: course_quiz_questions_search_Api,
        onError: notifyOnError(t),
        onSuccess: (result, {response}) => {
            const items = (response && response.data && response.data.data) || []
            setResults(items)
            setOpen(items.length > 0)
        },
    })

    const debouncedSearchRef = useRef(null)
    if (!debouncedSearchRef.current) {
        debouncedSearchRef.current = _.debounce((kw, fn) => fn(kw), 400)
    }

    useEffect(() => {
        debouncedSearchRef.current(keyword, (kw) => {
            if (kw.trim().length >= 2) {
                doSearch({q: kw, limit: 15})
            } else {
                setResults([])
                setOpen(false)
            }
        })
    }, [keyword])

    // Đóng dropdown khi click ra ngoài
    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [])

    // Tính toán vị trí fixed để thoát khỏi sticky/overflow context
    useEffect(() => {
        if (open && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect()
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
            })
        }
    }, [open])

    const typeLabel = (type) => {
        const map = {single: t('Single'), multiple: t('Multiple'), bool: t('True/False'), essay: t('Essay')}
        return map[type] || type
    }
    const typeColor = (type) => {
        const map = {single: 'bg-blue-100 text-blue-700', multiple: 'bg-purple-100 text-purple-700', bool: 'bg-amber-100 text-amber-700', essay: 'bg-green-100 text-green-700'}
        return map[type] || 'bg-gray-100 text-gray-600'
    }

    const handleUse = (q) => {
        onUseQuestion({
            id: genId(),
            type: q.type,
            q: q.question_text,
            max_score: q.max_score,
            ops: q.ops ?? [],
        })
        setKeyword('')
        setResults([])
        setOpen(false)
    }

    return (
        <div ref={wrapperRef} className="relative">
            <div className="flex items-center gap-2 px-3 py-2 border border-color-100 background rounded-xl focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-200 transition-all">
                {isLoading
                    ? <Spin size="small"/>
                    : <BsSearch size={13} className="text-color-400 flex-shrink-0"/>
                }
                <input
                    type="text"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder={`${t('Search existing questions to reuse')}...`}
                    className="flex-1 bg-transparent text-sm text-color-100 placeholder-color-400 outline-none border-0"
                />
                {keyword && (
                    <button onClick={() => {setKeyword(''); setResults([]); setOpen(false)}}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-xs flex-shrink-0">
                        ×
                    </button>
                )}
            </div>

            {open && results.length > 0 && (
                <div
                    style={dropdownStyle}
                    className="background border border-color-100 rounded-xl shadow-xl max-h-72 overflow-y-auto">
                    <div className="px-3 py-2 text-2xs font-semibold text-color-400 uppercase tracking-wider border-b border-color-50">
                        {results.length} {t('questions found')} — {t('click to reuse')}
                    </div>
                    {results.map((q, i) => (
                        <div key={q.id || i}
                            className="flex items-start gap-3 px-3 py-2.5 border-b border-color-50 last:border-0 hover:bg-indigo-50/60 transition-colors">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-color-100 leading-snug truncate">{q.question_text}</p>
                                <div className="flex items-center gap-1.5 mt-1">
                                    <span className={classNames('text-2xs font-semibold px-1.5 py-0.5 rounded', typeColor(q.type))}>
                                        {typeLabel(q.type)}
                                    </span>
                                    <span className="text-2xs text-color-400">{q.max_score}đ</span>
                                    {Array.isArray(q.ops) && q.ops.length > 0 && (
                                        <span className="text-2xs text-color-400">{q.ops.length} {t('options')}</span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => handleUse(q)}
                                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                                <BsLightningFill size={10}/>
                                {t('use')}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// --- Score Counter Bar ---
const ScoreCounterBar = ({questions}) => {
    const t = useTranslate()
    const {values, errors} = useFormikContext()
    const maxScore = parseFloat(values && values.max_score) || 0
    const isActivating = parseInt(values && values.status) === 1
    const totalScore = questions.reduce((sum, q) => sum + (parseFloat(q.max_score) || 0), 0)
    const diff = Math.round((totalScore - maxScore) * 100) / 100

    if (maxScore === 0 && questions.length === 0) return null

    const isMatch = Math.abs(diff) < 0.001
    const isOver = diff > 0
    const hasError = !!(errors && errors.questions_json)

    return (
        <div className="flex flex-col gap-1">
            <div className={classNames(
                'flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                isMatch ? 'bg-green-50 border-green-200 text-green-700'
                    : isOver ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-amber-50 border-amber-200 text-amber-700'
            )}>
                <span>
                    {t('Total score')}: <strong>{totalScore}đ</strong>
                    {maxScore > 0 && <span className="text-xs font-normal ml-1">/ {maxScore}đ {t('max')}</span>}
                </span>
                <span className="text-xs">
                    {isMatch ? `✓ ${t('Score matches')}` : isOver ? `+${diff}đ ${t('over limit')}` : `${diff}đ ${t('remaining')}`}
                </span>
            </div>
            {hasError && isActivating && (
                <p className="text-xs text-red-600 px-1">{errors.questions_json}</p>
            )}
        </div>
    )
}

// --- Main Component: QuestionBuilderField ---
function QuestionBuilderField({name, value, onChange}) {
    const t = useTranslate()
    const [questions, setQuestions] = useState(() => {
        let initialValue = []
        try {
            initialValue = Array.isArray(value)
                ? value
                : (typeof value === 'string' && value.trim() ? JSON.parse(value) : [])
        } catch (e) {}

        return initialValue.map(q => {
            if ((q.type === 'single' || q.type === 'multiple') && Array.isArray(q.ops)) {
                q.ops = q.ops.map((op, i) => ({...op, value: getLetterValue(i)}))
            }
            return {...q, id: q.id || genId()}
        })
    })

    useEffect(() => {
        const output = questions.map((question) => ({
            ...question,
            ops: (question.ops || []).filter(op => op?.label?.trim() !== '' || question?.type === 'bool'),
        }))
        if (JSON.stringify(output) !== JSON.stringify(value)) {
            try {
                onChange({target: {name, value: output}})
            } catch (e) {}
        }
    }, [questions])

    const handleQuestionUpdate = useCallback((index, updated) => {
        setQuestions(prev => prev.map((q, i) => i === index ? updated : q))
    }, [])

    const addQuestion = useCallback((indexToInsertAt) => {
        setQuestions(prev => [
            ...prev.slice(0, indexToInsertAt),
            defaultQuestion(),
            ...prev.slice(indexToInsertAt),
        ])
    }, [])

    const removeQuestion = useCallback((index) => {
        setQuestions(prev => prev.filter((_, i) => i !== index))
    }, [])

    const questionListRef = useRef(null)

    // Nhận câu hỏi từ ngân hàng (search reuse)
    const handleUseFromBank = useCallback((bankQuestion) => {
        setQuestions(prev => [bankQuestion, ...prev])
        // Scroll to show the newly added question below the sticky toolbar
        setTimeout(() => {
            if (questionListRef.current) {
                questionListRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
            }
        }, 50)
    }, [])

    return (
        <div>
            {/* Mô tả đầu tab - người dùng lần đầu không rõ tổng điểm câu phải khớp điểm tối đa,
                hay có thể tái dùng câu hỏi cũ. */}
            <div className="mb-3 px-3 py-2 flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                <BsInfoCircle size={14} className="mt-0.5 text-indigo-500 flex-shrink-0"/>
                <span className="text-xs text-indigo-700 leading-snug">
                    {t('question set tab guide')}
                </span>
            </div>
            {/* Sticky toolbar */}
            <div className="mb-4 p-3 border border-color-50 background shadow-sm rounded-xl sticky top-0 z-10 flex flex-col gap-2">
                <QuizQuestionSearchBar onUseQuestion={handleUseFromBank}/>
                <ScoreCounterBar questions={questions}/>
                <button
                    type="button"
                    onClick={() => addQuestion(0)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-500 hover:bg-indigo-600 rounded-xl transition-colors">
                    <BsPlusLg size={14}/>
                    {t('add new question')}
                </button>
            </div>

            {/* Question List */}
            <div ref={questionListRef} className="max-w-4xl mx-auto">
                {questions.length > 0 ? (
                    questions.map((question, index) => (
                        <React.Fragment key={question.id}>
                            <QuestionCard
                                question={question}
                                index={index}
                                onQuestionUpdate={handleQuestionUpdate}
                                onQuestionRemove={removeQuestion}
                                errors={validateQuestion(question)}
                            />
                            <InlineAddButton index={index} onAdd={addQuestion}/>
                        </React.Fragment>
                    ))
                ) : (
                    <div className="p-8 text-center rounded-xl border border-dashed border-color-100">
                        <p className="font-semibold text-color-200">{t('No questions yet.')}</p>
                        <p className="mt-1 text-xs text-color-400">{t('Search for existing questions above or click Add New Question.')}</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export const QuestionsJSON = ({defaultValue, onChange}) => {
    const [questionData, setQuestionData] = useState(defaultValue)

    const handleChange = (event) => {
        const val = event.target.value
        setQuestionData(val)
        onChange(val)
    }

    return (
        <QuestionBuilderField
            name="questions_json"
            value={questionData}
            onChange={handleChange}
        />
    )
}


// Field component tái dùng NGUYÊN từ formSchema (Input/TimePicker/DatePicker/InputNumber đã viết
// sẵn) - chỉ đổi khung Section/Field bọc ngoài sang Modern Form + thêm hint giải thích ý nghĩa từng
// field (bạn yêu cầu), không viết lại input logic.
const TitleInput = formSchema.title.children[0].component
const DescriptionInput = formSchema.description.children[0].component
const DurationInput = formSchema.duration_minutes.children[0].component
const TimeOpenInput = formSchema.started_finished_date.children[0].component
const TimeCloseInput = formSchema.started_finished_date.children[1].component
const MaxScoreInput = formSchema.score.children[0].component
const PassedScoreInput = formSchema.score.children[1].component
const MaxAttemptsInput = formSchema.max_attempts.children[0].component

// Hoist Ở MODULE SCOPE (không định nghĩa inline trong render của CourseClassExamUpdateForm) -
// ActionModalWrapper dùng field.component làm React component TYPE, định nghĩa inline sẽ tạo
// reference mới mỗi lần re-render (mỗi keystroke) -> remount cả tab đang active -> mất focus input
// (cùng bẫy đã ghi chú ở ParishForm.js/CourseClassStudentForm.js). Đọc values qua useFormikContext()
// bên trong thay vì nhận qua closure/props - tab "questions" bên dưới vẫn giữ nguyên FieldsFactory cũ
// (không thuộc phạm vi yêu cầu lần này).
const CourseClassExamOverviewTab = () => {
    const t = useTranslate()
    const {values, errors, touched, setFieldValue} = useFormikContext()
    const onChange = (e) => setFieldValue(e.target.name, e.target.value)

    return (
        <div className="flex flex-col gap-4">
            {/* Trả lời thẳng "bài thi sau khi tạo nằm ở đâu" NGAY TRONG form - không chỉ nói miệng,
                để bất kỳ admin nào mở form này sau đó cũng biết mà không cần hỏi lại. */}
            <div className="flex gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                <IoInformationCircleOutline size={18} className="mt-0.5 text-blue-500 flex-shrink-0"/>
                <div className="flex flex-col gap-0.5">
                    <span className="font-semibold text-sm text-blue-700">{t('exam location hint title')}</span>
                    <span className="text-xs text-blue-600 leading-relaxed">{t('exam location hint')}</span>
                </div>
            </div>

            <Section icon={IoDocumentTextOutline} title={t('exam title')} theme="highlight">
                <Field label={t('exam title')} required error={touched.title && errors.title}>
                    <TitleInput name="title" value={values.title} onChange={onChange}/>
                </Field>
                <Field label={t('short description')}>
                    <DescriptionInput name="description" value={values.description} onChange={onChange}/>
                    <span className="text-2xs text-color-400 italic leading-relaxed">{t('exam description hint')}</span>
                </Field>
            </Section>

            <Section icon={IoIosTimer} title={t('duration (minutes)')} subtitle={t('exam time window hint')}>
                <Field label={t('duration (minutes)')}>
                    <DurationInput name="duration_minutes" value={values.duration_minutes} onChange={onChange}/>
                    <span className="text-2xs text-color-400 italic leading-relaxed">{t('exam duration hint')}</span>
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={t('started at')}>
                        <TimeOpenInput name="time_open" value={values.time_open} onChange={onChange}/>
                    </Field>
                    <Field label={t('finished at')}>
                        <TimeCloseInput name="time_close" value={values.time_close} onChange={onChange}/>
                    </Field>
                </div>
                <Field label={t('max No of attempts')}>
                    <MaxAttemptsInput name="max_attempts" value={values.max_attempts} onChange={onChange}/>
                    <span className="text-2xs text-color-400 italic leading-relaxed">{t('exam max attempts hint')}</span>
                </Field>
            </Section>

            <Section icon={IoStatsChartOutline} title={t('max score')}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={t('max score')} error={touched.max_score && errors.max_score}>
                        <MaxScoreInput name="max_score" value={values.max_score} onChange={onChange}/>
                        <span className="text-2xs text-color-400 italic leading-relaxed">{t('exam max score hint')}</span>
                    </Field>
                    <Field label={t('passed score')} error={touched.passed_score && errors.passed_score}>
                        <PassedScoreInput name="passed_score" value={values.passed_score} onChange={onChange}/>
                        <span className="text-2xs text-color-400 italic leading-relaxed">{t('exam passed score hint')}</span>
                    </Field>
                </div>
            </Section>

            <Section icon={IoShieldCheckmarkOutline} title={t('status')}>
                <Field>
                    <ChipGroup
                        required
                        options={EXAM_STATUS_OPTIONS}
                        value={values.status}
                        onChange={(next) => setFieldValue('status', next)}
                    />
                </Field>
                <div className="p-2.5 background-50 rounded-lg space-y-1">
                    <p className="text-2xs text-color-300 leading-relaxed">{t('exam status meaning hint')}</p>
                    <p className="text-2xs text-color-300 leading-relaxed">
                        <span className="font-semibold text-color-400">{`${t('note')}: `}</span>
                        {t('active_status_requirement')}
                    </p>
                </div>
            </Section>
        </div>
    )
}

export const CourseClassExamUpdateForm = ({
                                              course_class_exam,
                                              initialValues,
                                              onSubmit = NullAsDefault,
                                              isSubmitting = false,
                                          }) => {
    const t = useTranslate()
    const [current, setCurrent] = useState('overview')

    const status_options = EXAM_STATUS_OPTIONS

    const _initialValues = useMemo(() => {
        const {score_options, grade_date, ...rest} = initialValues ?? {}
        return {
            ...rest,
            questions_json: rest?.questions || [],
            status_options: status_options.map(deepTranslate(t)),
            course_class_exam_id: rest?.id,
        }
    }, [initialValues, course_class_exam?.id])

    const validate = (values) => {
        let errors = {}

        const {title, status, max_score, passed_score, questions_json = []} = values ?? {}

        if (_.isEmpty(title ?? '')) {
            errors.title = t('required field')
        }

        if (
            questions_json.some((item) => _.isEmpty(item.q?.trim()))
        ) {
            errors.questions_json = t('Question content is required')
        }

        // Kiểm tra passed_score > max_score
        const examMax = parseFloat(max_score) || 0
        const passedScore = parseFloat(passed_score) || 0

        if (passedScore > examMax) {
            errors.passed_score = t('Cannot exceed max score')
        }

        // Khi kích hoạt (status=1), validate tổng điểm câu = max_score
        if (parseInt(status) === 1) {
            const questions = Array.isArray(questions_json) ? questions_json : []
            if (questions.length === 0) {
                errors.questions_json = t('Exam must have at least 1 question before activating')
            } else {
                const totalScore = questions.reduce((sum, q) => sum + (parseFloat(q.max_score) || 0), 0)
                const examMax = parseFloat(max_score) || 0
                if (examMax > 0 && Math.abs(totalScore - examMax) > 0.001) {
                    errors.questions_json = `${t('total question score')} (${totalScore}) ${t('must equal exam max score')} (${examMax})`
                }
            }
        }

        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={({status_options, ...values}) => {
                const {time_open, time_close} = values
                const changes = shallowDiff(values, initialValues)
                const {questions_json, ...rest} = changes ?? {};
                const _values = _.omitBy(
                    {
                        ...rest,
                        questions_json: JSON.stringify(questions_json)
                    },
                    _.isUndefined
                );
                onSubmit(
                    {
                        ..._values,
                        time_open,
                        time_close
                    }
                )
            }}>
            <ActionModalWrapper
                tab={current}
                action={<FormActionBar submitText="save"/>}
                onTabChange={(key) => {
                    setCurrent(key)
                }}
                tabs={[
                    {
                        key: 'overview',
                        label: 'overview',
                        component: CourseClassExamOverviewTab,
                    },
                    {
                        key: 'questions',
                        label: 'questions',
                        component: () => (
                            <FieldsFactory
                                formSchema={[
                                    formSchema.questions_json,
                                ]}
                            />
                        ),
                    },
                ]}>
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.description,
                            formSchema.duration_minutes,
                            formSchema.score,
                            formSchema.max_attempts,
                            formSchema.started_finished_date,
                            formSchema.status
                        ]}
                    />
                </Pure>
            </ActionModalWrapper>
        </Formik>
    )
}



